import React, { useState, useEffect } from "react";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import Loader from "../../../../components/Common/Loader";
import {
  DELETEPRICE,
  ROOMLIST,
  ROOMPRICING,
  ROOMTYPE,
} from "../../../../services/ApiCalls";
import Slider from "react-slick";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const roomSchema = z.object({
  roomId: z.string().min(1, { message: "Please select a room type" }),
  ratePricing: z.array(
    z.object({
      durationType: z.string().min(1, { message: "Duration type required" }),
      basePrice: z
        .number()
        .min(0, { message: "Base price cannot be negative" }),
      discount: z
        .number()
        .min(0, { message: "Discount cannot be negative" })
        .max(100, { message: "Discount cannot exceed 100%" }),
      refundable: z.boolean(),
    })
  ),
});

const displayValue = (value, fallback = "-") =>
  value === null || value === undefined || value === "" ? fallback : value;

const getImageSrc = (image) => image?.link || image?.url || image;

const getRoomTypeName = (room) =>
  room?.type?.roomType?.name || room?.type?.name || "-";

const getRoomPricing = (room) => {
  if (room?.pricingDetail?.length) return room.pricingDetail;
  if (room?.type?.pricingData?.length) return room.type.pricingData;
  if (room?.pricing?.length) return room.pricing;
  if (room?.ratePricing?.length) return room.ratePricing;
  return [];
};

const getPriceDuration = (rate) => {
  if (rate?.durationHours) return `${rate.durationHours} hour`;
  if (rate?.duration) {
    return rate.pricingType === "day"
      ? `${rate.duration} day`
      : `${rate.duration} hour`;
  }
  return rate?.durationType || "-";
};

const RoomDetail = () => {
  const [roomDetail, setRoomDetail] = useState();
  const [loader, setLoader] = useState(false);
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const page = queryParams.get("page") || 1;
  const [hotelAdminData, setHotelAdminData] = useState([]);
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(roomSchema),
    defaultValues: {
      roomId: "",
      ratePricing: [
        { durationType: "", basePrice: 0, discount: 0, refundable: true },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "ratePricing",
  });

  const getAmenityData = async () => {
    try {
      const roomRes = await ROOMTYPE({ limit: 100, page: 1 });
      if (roomRes?.data?.status === "success") {
        setHotelAdminData(roomRes?.data?.data);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const getData = async () => {
    try {
      setLoader(true);
      const res = await ROOMLIST({ id });
      if (res?.data?.status === "success") {
        setRoomDetail(res.data?.data);
      }
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    getAmenityData();
    if (id) getData();
  }, [id]);

  const submitHandler = async (data) => {
    setLoader(true);
    const payload = {
      ...data,
      roomId: id,
    };
    try {
      const res = await ROOMPRICING(payload);
      if (res?.data?.status === "success") {
        toast.success(res?.data?.message);
        getData();
      } else {
        toast.error(res?.data?.message);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoader(false);
    }
  };

  const handleDeleteRate = async (data) => {
    const payload = {
      id: id,
      priceId: data,
    };

    let res = await DELETEPRICE(payload);
    if (res?.data?.status === "success") {
      toast.success(res?.data?.message);
      getData();
    } else {
      toast.error(res?.data?.message);
    }
  };

  const handleCancel = () => navigate("/room");

  console.log(roomDetail, "roomDetail");
  const roomImages = roomDetail?.roomImages?.length
    ? roomDetail.roomImages
    : roomDetail?.images || [];
  const roomPricing = getRoomPricing(roomDetail);

  return (
    <>
      {loader && <Loader />}
      <section className="roomDetail py-3 position-relative">
        <Container>
          <Row>
            <Col lg="12">
              <div className="d-flex align-items-center gap-2">
                <Link
                  to={`/room?page=${page}`}
                  className="border d-flex align-items-center p-2 rounded"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 10 16"
                    fill="none"
                  >
                    <path
                      d="M8.64707 0.473344C8.55514 0.381188 8.44594 0.308072 8.32572 0.258184C8.20549 0.208296 8.07661 0.182617 7.94644 0.182617C7.81628 0.182617 7.68739 0.208296 7.56717 0.258184C7.44694 0.308072 7.33774 0.381188 7.24582 0.473344L0.667065 7.05209C0.593675 7.12533 0.53545 7.21233 0.495723 7.3081C0.455996 7.40387 0.435547 7.50654 0.435547 7.61022C0.435547 7.7139 0.455996 7.81657 0.495723 7.91234C0.53545 8.00811 0.593675 8.0951 0.667065 8.16834L7.24582 14.7471C7.63373 15.135 8.25915 15.135 8.64707 14.7471C9.03498 14.3592 9.03498 13.7338 8.64707 13.3458L2.9154 7.60626L8.65498 1.86668C9.03498 1.48668 9.03498 0.853344 8.64707 0.473344Z"
                      fill="#E8307A"
                      stroke="#E8307A"
                      strokeWidth="0.2"
                    />
                  </svg>
                </Link>
                <h4 className="mb-0 py-3 fw-bold text-dark text-capitalize">
                  {t("manageRoom.detailTitle")}
                </h4>
              </div>
            </Col>
            <Col lg="12" className="my-2">
              <Card className="p-3 shadow-sm rounded">
                <h5 className="fw-bold mb-3">
                  {t("manageRoom.roomImages", "Room Images")}
                </h5>

                {roomImages?.length > 0 ? (
                  roomImages.length === 1 ? (
                    <div className="p-2">
                      <img
                        src={getImageSrc(roomImages[0])}
                        alt="room-0"
                        className="rounded shadow-sm"
                        style={{
                          width: "100%",
                          height: 300,
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  ) : (
                    <Slider
                      dots={true}
                      infinite={roomImages.length > 1}
                      speed={500}
                      slidesToShow={Math.min(roomImages.length, 2)}
                      slidesToScroll={1}
                      arrows={roomImages.length > 1}
                      responsive={[
                        {
                          breakpoint: 1024,
                          settings: {
                            slidesToShow: Math.min(roomImages.length, 3),
                          },
                        },
                        {
                          breakpoint: 768,
                          settings: {
                            slidesToShow: Math.min(roomImages.length, 2),
                          },
                        },
                        { breakpoint: 480, settings: { slidesToShow: 1 } },
                      ]}
                    >
                      {roomImages.map((src, i) => (
                        <div key={i} className="p-2">
                          <img
                            src={getImageSrc(src)}
                            alt={`room-${i}`}
                            className="rounded shadow-sm"
                            style={{
                              width: "100%",
                              height: 200,
                              objectFit: "cover",
                            }}
                          />
                        </div>
                      ))}
                    </Slider>
                  )
                ) : (
                  <p className="text-muted">
                    {t("common.noImages", "No Images")}
                  </p>
                )}
              </Card>
            </Col>

            <Col lg="12" className="my-2">
              <div
                className="formWrpper px-lg-5 p-md-4 p-3 rounded"
                style={{ background: "#EEEEEE" }}
              >
                {roomDetail ? (
                  <Row className="justify-content-between">
                    <Col md={6} className="my-2">
                      <ul className="list-unstyled ps-0 mb-0 notLastBorder pe-lg-3">
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.columns.hotelName")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {displayValue(roomDetail?.hotelId?.hotelName)}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageHotel.columns.hotelId")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {displayValue(roomDetail?.hotelId?.hotelId)}
                          </h6>
                        </li>
                        {/* <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.columns.floorNumber")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {displayValue(roomDetail?.floorNumber)}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.columns.roomNumber")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {displayValue(roomDetail?.roomNumber)}
                          </h6>
                        </li> */}
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.columns.roomType")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {getRoomTypeName(roomDetail)}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.columns.bedType")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {displayValue(roomDetail?.bedType)}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.columns.createdAt")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {moment(roomDetail?.createdAt).format("YYYY-MM-DD")}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.columns.status")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {displayValue(roomDetail?.status)}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.totalRoom", "Total Room")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {displayValue(
                              roomDetail?.totalRoom ?? roomDetail?.noOfRoom
                            )}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.availableRoom", "Available Room")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {displayValue(roomDetail?.availableRoom)}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.rating")}:
                          </p>
                         <h6 className="m-0 text-muted fw-bold w-50">
                            {displayValue(roomDetail?.avgRating)}
                          </h6>
                        </li>
                      </ul>
                    </Col>

                    <Col md={6} className="my-2">
                      <ul className="list-unstyled mb-0 notLastBorder ps-lg-3">
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.address")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {displayValue(roomDetail?.hotelId?.address)}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.updatedAt")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {moment(roomDetail?.updatedAt).format("YYYY-MM-DD")}
                          </h6>
                        </li>
                        {/* <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.beds", "Number of Beds.")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {roomDetail?.numberOfBed}
                          </h6>
                        </li> */}
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t(
                              "manageRoom.columns.numberOfPeople",
                              "Number of People Allowed."
                            )}
                            :
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {displayValue(roomDetail?.maxOccupancy)}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.childrenAllowed", "Children Allowed")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {displayValue(
                              roomDetail?.maxChilOccupancy ??
                                roomDetail?.maxChildOccupancy
                            )}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.extraAdultPrice", "Extra Adult Price")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {displayValue(roomDetail?.extraAdultPrice)}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.extraChildPrice", "Extra Child Price")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {displayValue(roomDetail?.extraChildPrice)}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.roomSize", "Room Size")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {displayValue(roomDetail?.length)} x{" "}
                            {displayValue(roomDetail?.width)}
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-center gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.area", "Area")}:
                          </p>
                          <h6 className="m-0 text-muted fw-bold w-50">
                            {displayValue(roomDetail?.area)} m2
                          </h6>
                        </li>
                        <li className="py-3 d-flex align-items-start gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.amenity")}:
                          </p>
                          <div className="m-0 text-muted w-50 d-flex flex-wrap gap-2">
                            {roomDetail?.amenities?.map((a) => (
                              <span key={a._id} className="badge bg-secondary">
                                {a.name}
                              </span>
                            ))}
                          </div>
                        </li>
                        <li className="py-3 d-flex align-items-start gap-10">
                          <p className="m-0 themeBlue fw-sbold w-50">
                            {t("manageRoom.notes")}:
                          </p>
                          <p
                            className="m-0 text-muted w-50"
                            style={{ fontSize: 12, lineHeight: "18px" }}
                          >
                            {displayValue(roomDetail?.housekeepingNotes)}
                          </p>
                        </li>

                      </ul>
                    </Col>

                    <Col lg="12" className="my-2">
                      <p className="m-0 themeBlue fw-sbold">
                        {t("manageRoom.rateprice")}
                      </p>
                      {roomPricing.length > 0 ? (
                        roomPricing.map((rate, index) => (
                          <div
                            key={rate._id || index}
                            className="border rounded p-2 mb-2 d-flex justify-content-between align-items-center"
                            style={{ fontSize: 13, lineHeight: "20px" }}
                          >
                            <div style={{ flex: 1 }}>
                              <Row>
                                <Col md={3}>
                                  <strong>{t("manageRoom.duration")}:</strong>{" "}
                                  {getPriceDuration(rate)}
                                </Col>
                                <Col md={2}>
                                  <strong>
                                    {t("manageRoom.pricingType", "Type")}:
                                  </strong>{" "}
                                  {displayValue(rate?.pricingType)}
                                </Col>
                                <Col md={2}>
                                  <strong>
                                    {t(
                                      "manageRoom.availableFrom",
                                      "Available From"
                                    )}
                                    :
                                  </strong>{" "}
                                  {displayValue(rate?.checkInTime?.from)}
                                </Col>
                                <Col md={2}>
                                  <strong>
                                    {t("manageRoom.availableTill", "Available Till")}:
                                  </strong>{" "}
                                  {displayValue(rate?.checkInTime?.to)}
                                </Col>
                                <Col md={2}>
                                  <strong>{t("manageRoom.base")}:</strong>{" "}
                                  {displayValue(
                                    rate?.basePrice ?? rate?.weekdayPrice
                                  )}
                                </Col>
                                <Col md={2}>
                                  <strong>
                                    {t(
                                      "manageRoom.saturdayPrice",
                                      "Saturday Price"
                                    )}
                                    :
                                  </strong>{" "}
                                  {displayValue(rate?.saturdayPrice)}
                                </Col>
                                <Col md={2}>
                                  <strong>
                                    {t(
                                      "manageRoom.sundayPrice",
                                      "Sunday Price"
                                    )}
                                    :
                                  </strong>{" "}
                                  {displayValue(rate?.sundayPrice)}
                                </Col>
                                <Col md={2}>
                                  <strong>{t("manageRoom.discount")}:</strong>{" "}
                                  {displayValue(rate?.discount, 0)}%
                                </Col>
                                <Col md={3}>
                                  <strong className="text-nowrap">
                                    {t("manageRoom.final")}:
                                  </strong>{" "}
                                  {displayValue(
                                    rate?.finalPrice !== undefined
                                      ? Number(rate.finalPrice).toFixed(2)
                                      : undefined
                                  )}
                                </Col>
                                <Col md={2}>
                                  <strong>
                                    {t("manageRoom.minStay", "Min Stay")}:
                                  </strong>{" "}
                                  {displayValue(rate?.minStay)}
                                </Col>
                                <Col md={2}>
                                  <strong>
                                    {t("manageRoom.active", "Active")}:
                                  </strong>{" "}
                                  {typeof rate?.isActive === "boolean"
                                    ? rate.isActive
                                      ? "Yes"
                                      : "No"
                                    : "-"}
                                </Col>
                              </Row>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-muted" style={{ fontSize: 12 }}>
                          {t("manageRoom.norateProvide")}
                        </p>
                      )}
                    </Col>

                    {/* <Col lg="6">
                      <label className="fw-bold mb-2">Refund Policy</label>
                      <div className="border rounded p-3 bg-light">
                        {roomDetail?.refundPolicy ? (
                          <div className="d-flex flex-column gap-2">
                            <div className="d-flex justify-content-between align-items-center">
                              <span
                                className={`badge ${
                                  roomDetail.refundPolicy.type === "FULL_REFUND"
                                    ? "bg-success"
                                    : roomDetail.refundPolicy.type ===
                                      "PARTIAL_REFUND"
                                    ? "bg-warning"
                                    : "bg-danger"
                                }`}
                              >
                                {roomDetail.refundPolicy.type?.replace(
                                  "_",
                                  " "
                                )}
                              </span>
                              {roomDetail.refundPolicy.cutoffHours && (
                                <small className="text-muted">
                                  CutOff Hours{" "}
                                  {roomDetail.refundPolicy.cutoffHours} h
                                </small>
                              )}
                            </div>

                            {roomDetail.refundPolicy.description && (
                              <p className="mb-0 small">
                                {roomDetail.refundPolicy.description}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted">
                            No refund policy specified
                          </span>
                        )}
                      </div>
                    </Col> */}
                    {/* <Col lg="12" className="my-2">
                      <p className="m-0 themeBlue fw-sbold">
                        {t("manageRoom.rateprice")}
                      </p>
                      {roomDetail?.ratePricing?.length > 0 ? (
                        roomDetail.ratePricing.map((rate, index) => (
                          <div
                            key={rate._id || index}
                            className="border rounded p-2 mb-2 d-flex justify-content-between align-items-center"
                            style={{ fontSize: 13, lineHeight: "20px" }}
                          >
                            <div style={{ flex: 1 }}>
                              <Row>
                                <Col md={3}>
                                  <strong>{t("manageRoom.duration")}:</strong>{" "}
                                  {rate?.durationType}
                                </Col>
                                <Col md={2}>
                                  <strong>{t("manageRoom.base")}:</strong>{" "}
                                  {rate?.basePrice}
                                </Col>
                                <Col md={2}>
                                  <strong>{t("manageRoom.discount")}:</strong>{" "}
                                  {rate?.discount}%
                                </Col>
                                <Col md={2}>
                                  <strong className="text-nowrap">
                                    {t("manageRoom.final")}:
                                  </strong>{" "}
                                  {rate?.finalPrice.toFixed(2)}
                                </Col>
                                <Col md={3}>
                                  <strong>{t("manageRoom.refundable")}:</strong>{" "}
                                  {rate?.refundable ? "Yes" : "No"}
                                </Col>
                              </Row>
                            </div>

                            <div className="ms-2">
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() =>
                                  handleDeleteRate(rate._id || index)
                                }
                              >
                                {t("common.delete", "Delete")}
                              </Button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-muted" style={{ fontSize: 12 }}>
                          {t("manageRoom.norateProvide")}
                        </p>
                      )}
                    </Col> */}

                    {/* <Col lg="12" className="my-2">
                      <div
                        className="formWrpper rounded"
                        style={{ background: "#EEEEEE" }}
                      >
                        <span className="fw-bold mb-2 d-block">
                          {t("manageRoom.addPrice")}
                        </span>
                        <Form onSubmit={handleSubmit(submitHandler)}>
                          <Row>
                            <Col lg="6" className="my-2">
                              <label>{t("manageRoom.selectRoomType")}</label>
                              <select
                                className="form-control"
                                {...register("roomId")}
                              >
                                <option value="">
                                  {t("common.selectRoom")}
                                </option>
                                {hotelAdminData?.map((room) => (
                                  <option key={room._id} value={room._id}>
                                    {room?.name || t("common.na")}
                                  </option>
                                ))}
                              </select>
                              {errors.roomId && (
                                <p className="text-danger">
                                  {errors.roomId.message}
                                </p>
                              )}
                            </Col>

                            <Col lg="12" className="my-2">
                              <h6>{t("manageRoom.ratePricing")}</h6>
                              {fields.map((field, index) => (
                                <Row
                                  key={field.id}
                                  className="align-items-center mb-3"
                                >
                                  <Col md={3}>
                                    <label>
                                      {t("manageRoom.durationType")}
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      {...register(
                                        `ratePricing.${index}.durationType`
                                      )}
                                    />
                                    {errors.ratePricing?.[index]
                                      ?.durationType && (
                                      <p className="text-danger">
                                        {
                                          errors.ratePricing[index].durationType
                                            .message
                                        }
                                      </p>
                                    )}
                                  </Col>

                                  <Col md={2}>
                                    <label>{t("manageRoom.basePrice")}</label>
                                    <input
                                      type="number"
                                      className="form-control"
                                      {...register(
                                        `ratePricing.${index}.basePrice`,
                                        {
                                          valueAsNumber: true,
                                          min: {
                                            value: 0,
                                            message: t(
                                              "validation.basePriceNegative"
                                            ),
                                          },
                                        }
                                      )}
                                      min={0}
                                    />
                                    {errors.ratePricing?.[index]?.basePrice && (
                                      <p className="text-danger">
                                        {
                                          errors.ratePricing[index].basePrice
                                            .message
                                        }
                                      </p>
                                    )}
                                  </Col>

                                  <Col md={2}>
                                    <label>{t("manageRoom.discount")}</label>
                                    <input
                                      type="number"
                                      className="form-control"
                                      {...register(
                                        `ratePricing.${index}.discount`,
                                        {
                                          valueAsNumber: true,
                                          min: {
                                            value: 0,
                                            message: t(
                                              "validation.discountNegative"
                                            ),
                                          },
                                          max: {
                                            value: 100,
                                            message: t(
                                              "validation.discountExceed"
                                            ),
                                          },
                                        }
                                      )}
                                      min={0}
                                      max={100}
                                    />
                                    {errors.ratePricing?.[index]?.discount && (
                                      <p className="text-danger">
                                        {
                                          errors.ratePricing[index].discount
                                            .message
                                        }
                                      </p>
                                    )}
                                  </Col>

                                  <Col
                                    md={2}
                                    className="d-flex align-items-center"
                                  >
                                    <Form.Check
                                      type="checkbox"
                                      label={t("manageRoom.refundable")}
                                      {...register(
                                        `ratePricing.${index}.refundable`
                                      )}
                                    />
                                  </Col>

                                  <Col
                                    md={2}
                                    className="d-flex align-items-center"
                                  >
                                    <Button
                                      variant="danger"
                                      onClick={() => remove(index)}
                                    >
                                      {t("common.remove")}
                                    </Button>
                                  </Col>
                                </Row>
                              ))}

                              <Button
                                variant="primary"
                                onClick={() =>
                                  append({
                                    durationType: "",
                                    basePrice: 0,
                                    discount: 0,
                                    refundable: true,
                                  })
                                }
                              >
                                {t("common.addRate")}
                              </Button>
                            </Col>

                            <Col
                              lg="12"
                              className="my-2 d-flex justify-content-center gap-2"
                            >
                              <Button
                                type="button"
                                onClick={handleCancel}
                                variant="secondary"
                              >
                                {t("common.cancel")}
                              </Button>
                              <Button type="submit" variant="primary">
                                {t("common.submit")}
                              </Button>
                            </Col>
                          </Row>
                        </Form>
                      </div>
                    </Col> */}
                  </Row>
                ) : (
                  <p className="text-center text-muted">
                    No room details found
                  </p>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default RoomDetail;
